import { useTheme } from '@mui/material/styles';
import React, { useId, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { map } from './core/MapView';
import getSpeedColor from '../common/util/colors';
import { useAttributePreference } from '../common/util/preferences';

const MapRoutePath = ({ positions }) => {
  const id = useId();

  const theme = useTheme();

  // 2ECOMP MODIFIED: Selector otimizado - extrai deviceId uma vez e usa memoização
  const firstDeviceId = positions?.[0]?.deviceId;
  const reportColor = useSelector((state) => {
    if (!firstDeviceId) return null;
    return state.devices.items[firstDeviceId]?.attributes?.['web.reportColor'] || null;
  });

  const mapLineWidth = useAttributePreference('mapLineWidth', 2);
  const mapLineOpacity = useAttributePreference('mapLineOpacity', 1);

  useEffect(() => {
    map.addSource(id, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [],
        },
      },
    });
    map.addLayer({
      source: id,
      id: `${id}-line`,
      type: 'line',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': ['get', 'width'],
        'line-opacity': ['get', 'opacity'],
      },
    });

    return () => {
      if (map.getLayer(`${id}-line`)) {
        map.removeLayer(`${id}-line`);
      }
      if (map.getSource(id)) {
        map.removeSource(id);
      }
    };
  }, []);

  // 2ECOMP MODIFIED: Single-pass para min/max speed (evita 2 iterações)
  const { minSpeed, maxSpeed } = useMemo(() => {
    if (!positions.length) return { minSpeed: 0, maxSpeed: 0 };
    return positions.reduce((acc, p) => ({
      minSpeed: Math.min(acc.minSpeed, p.speed),
      maxSpeed: Math.max(acc.maxSpeed, p.speed),
    }), { minSpeed: Infinity, maxSpeed: -Infinity });
  }, [positions]);

  // 2ECOMP MODIFIED: Memoiza criação de features com validação de coordenadas
  const features = useMemo(() => {
    const result = [];
    for (let i = 0; i < positions.length - 1; i += 1) {
      const curr = positions[i];
      const next = positions[i + 1];

      // Skip if coordinates are invalid (null, undefined, or NaN)
      if (!Number.isFinite(curr.longitude) || !Number.isFinite(curr.latitude)
          || !Number.isFinite(next.longitude) || !Number.isFinite(next.latitude)) {
        continue;
      }

      result.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [[curr.longitude, curr.latitude], [next.longitude, next.latitude]],
        },
        properties: {
          color: reportColor || getSpeedColor(
            next.speed,
            minSpeed,
            maxSpeed,
          ),
          width: mapLineWidth,
          opacity: mapLineOpacity,
        },
      });
    }
    return result;
  }, [positions, reportColor, minSpeed, maxSpeed, mapLineWidth, mapLineOpacity]);

  // Atualiza dados do mapa
  useEffect(() => {
    map.getSource(id)?.setData({
      type: 'FeatureCollection',
      features,
    });
  }, [id, features]);

  return null;
};

// 2ECOMP MODIFIED: React.memo para evitar re-renders desnecessários
export default React.memo(MapRoutePath);
